import "leaflet/dist/leaflet.css";
import "./styles.css";
import { useState, useLayoutEffect, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import LZString from "lz-string";
import Map from "./Map";
import { getIndexData } from "../../api/FlaskAPI";
import {
  setDatePickerFrom,
  setDatePickerTo,
  setAreaStats,
  setAreaTiles,
  clearAllMapState,
  clearDates,
  setDownloadLink,
  setFileContent,
  setUploaderFlag,
  setShowOverlay,
} from "../../reducers/MapSlice";
import { setUser } from "../../reducers/userSlice";
import SignInComponent from "../../components/Sign/SignComponent";
import CollapsibleImage from "../../components/CollapsibleComponent/CollapsibleImage";
import CollapsibleChart from "../../components/CollapsibleComponent/CollapsibleChart";
import IndexLegend from "../../components/IndexLegend";
import { useTranslation } from "react-i18next";
import { DateRangePicker } from "rsuite";
import { CustomProvider } from "rsuite";
import arEG from "rsuite/locales/ar_EG";
import useApi from "../../hooks/useApi";
import { useMap } from "react-leaflet/hooks";
import { useMemo } from "react";
import { toast } from "react-toastify";
import CoordinatesInput from "../../components/CoordinatesInput";
import NavPageSelector from "../../components/NavPageSelector";
import * as constants from "../../utils/constants";
import { getChartData, getWeatherChartdata } from "../../utils/charts_format";
import MobileIcon from "../../assets/images/onlyMobile.png";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import MonitoringDialog from "../Masking/MonitoringDialog";
import LandDegradationDialog from "../LandDegradation/LandDegradationDialog";
import IndexInfoDialog from "../../components/IndexInfoDialog";
import AddTiffDialog from "../../components/AddTiffDialog";
import { resetStore } from "../../reducers/resetStore";
import { url } from "../../api/axios_client";
import downloadIcon from "../../assets/images/download-icon.png";
import axios from "../../api/axios_client";
import { useAdmin } from "../../hooks/useAdmin";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import Stripe from "./Stripe";
import { CircularProgress, Typography, Box, useMediaQuery, useTheme } from "@mui/material";
let indexData;

const getLegendData = (dataKey) => {
  try {
    // Get and parse categories from localStorage
    const categoriesStr = localStorage.getItem("categories");
    const categories = JSON.parse(categoriesStr);

    // Find the index data that matches the dataKey
    indexData = categories.find((category) => {
      return (
        category.type.toLowerCase() === dataKey.toLowerCase() ||
        (category.category &&
          category.category.toLowerCase() === dataKey.toLowerCase()) ||
        (category.name && category.name.toLowerCase() === dataKey.toLowerCase())
      );
    });

    if (!indexData) {
      console.log("No matching index found for:", dataKey);
      return null;
    }

    if (!indexData) return null;

    // Parse the legends array
    const legends = indexData.legends.map((legendStr) => {
      // Remove single quotes and parse the string as JSON
      const cleanStr = legendStr.replace(/'/g, '"');
      const legendObj = JSON.parse(cleanStr);
      return {
        value: legendObj.label ?? legendObj.value,
        value_ar: legendObj.label_ar,
        color: legendObj.color,
      };
    });
    return {
      title: indexData.name,
      unit: indexData.unit,
      unit_ar: indexData.unit_ar,
      legends,
    };
  } catch (error) {
    console.error("Error parsing legend data:", error);
    return null;
  }
};

// Right-side panel for map controls + image info
function RightSidePanel({ areas, showOverlay, dispatch, t, fetchTiffImage, tiffLoading, datePickerTimeFrom, datePickerTimeTo }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumWidth = useMediaQuery(
    `(min-width:${theme.breakpoints.values.sm}px) and (max-width:${theme.breakpoints.values.lg}px)`
  );
  const isBelow1000 = useMediaQuery('(max-width:1000px)');
  const isTinyMobile = useMediaQuery('(max-width:387px)');
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const handleRemoveArea = () => {
    localStorage.removeItem('mapFileContent');
    dispatch(clearAllMapState());
    dispatch(setUploaderFlag(false));
    window.dispatchEvent(new Event('clearDrawnShapes'));
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: isTinyMobile ? 245 : (isBelow1000 ? 205 : 110),
        right: isArabic ? 'auto' : (isBelow1000 ? '0.8em' : '5em'),
        left: isArabic ? (isBelow1000 ? '0.8em' : '5em') : 'auto',
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 1,
        px: 2,
        borderRadius: "15px",
        backgroundColor: "rgba(255, 255, 255, 0.75)",
        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
        zIndex: 100,
        width: "fit-content",
        maxWidth: isMobile ? "70vw" : "280px",
      }}
    >
      {/* Control buttons */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <button className="delete-button" onClick={handleRemoveArea}>
          {t("Map.remove_file")}
        </button>
        <button
          className="delete-button"
          onClick={() => dispatch(setShowOverlay(!showOverlay))}
          style={{ backgroundColor: showOverlay ? '#329ace' : '#666' }}
        >
          {showOverlay ? t("Map.hide_overlay") || "Hide Overlay" : t("Map.show_overlay") || "Show Overlay"}
        </button>
      </Box>

      {/* Image info for areas with tiles */}
      {[...areas].slice(0, 2).map((area, idx) => {
        if (!area.tileUrl && !area.imagename) return null;
        return (
          <Box
            key={`${idx}-${area.imagename}`}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              py: 0.5,
              borderTop: "1px solid rgba(0,0,0,0.1)",
            }}
          >
            <Box sx={{ color: "#333", minWidth: 0, flex: 1, display: 'flex' }}>
              <span style={{ fontWeight: 600, fontSize: "13px", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', width: '100%' }}>
                {area.imagename || `Area ${idx + 1}`}
              </span>
            </Box>
            <button
              className="delete-button"
              onClick={() => !tiffLoading && fetchTiffImage(idx)}
              style={{
                cursor: tiffLoading ? "not-allowed" : "pointer",
                opacity: tiffLoading ? 0.6 : 1,
                padding: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "5px",
                width: "32px",
                height: "32px",
              }}
              title="Download TIFF"
            >
              {tiffLoading ? (
                <CircularProgress size={16} style={{ color: "white" }} />
              ) : (
                <img src={downloadIcon} alt="Download" style={{ width: 16, height: 16, filter: 'brightness(0) invert(1)' }} />
              )}
            </button>
          </Box>
        );
      })}
    </Box>
  );
}

export default function BaseBage({
  dataKey,
  monitoringKey,
  imageEndpoint,
  statsEndpoint,
  tiffEndopoint,
  route,
  legendImg,
  land_cover_dataset,
}) {
  const isAdmin = useAdmin();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [landDegradationOpen, setLandDegradationOpen] = useState(false);
  const [indexInfoOpen, setIndexInfoOpen] = useState(false);
  const [addTiffOpen, setAddTiffOpen] = useState(false);
  const [mainIndex, setMainIndex] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [tiffLoading, setTiffLoading] = useState(false); // TIFF loading state

  const handleOpen = () => {
    if (!areas || !areas[0] || !areas[0].coordinates) {
      toast.error("Please select the area you want to be monitored");
    } else {
      setMainIndex(monitoringKey);
      setOpen(true);
    }
  };
  const handleLandDegradationOpen = () => {
    if (!areas || !areas[0] || !areas[0].coordinates) {
      toast.error("Please select the area first");
    } else {
      setLandDegradationOpen(true);
    }
  };
  const handleIndexInfoOpen = () => {
    setIndexInfoOpen(true);
  };
  const handleAddTiffOpen = () => {
    setAddTiffOpen(true);
  };

  const handleClose = () => {
    // dispatch(resetStore());
    setOpen(false);
  };
  const { t, i18n } = useTranslation();
  const mapData = null;
  const legendData = getLegendData(dataKey);
  console.log("LEGGGGEEEND", legendData);
  const isWeatherPage = dataKey === "WeatherMonitoring";
  const loggedInUser = localStorage.getItem(constants.USER);
  const w = window.innerWidth;
  const h = window.innerHeight;
  const [isMobile, setIsMobile] = useState(w < 700);

  const [legendHidden, setLegendHidden] = useState(false);

  //user states
  const loggedUserState = useSelector((state) => state.userState.user);
  //map states
  const areas = useSelector((state) => state.mapState.areas);
  const datePickerTimeFrom = useSelector(
    (state) => state.mapState.date.datePickerTimeFrom
  );
  const datePickerTimeTo = useSelector(
    (state) => state.mapState.date.datePickerTimeTo
  );
  const downloadLink = useSelector((state) => state.mapState.downloadLinks);
  const showOverlay = useSelector((state) => state.mapState.showOverlay);

  const currentIndexId = localStorage.getItem("currentIndexId");

  // Helper function to get date range from localStorage for current index
  const getIndexDateRange = (indexId) => {
    if (!indexId) return { minDate: null, maxDate: null };
    try {
      const categories = JSON.parse(localStorage.getItem("categories")) || [];
      const currentIndex = categories.find((category) => category.id === indexId);
      if (currentIndex && currentIndex.available_from && currentIndex.available_to) {
        const minDate = new Date(currentIndex.available_from);
        minDate.setHours(0, 0, 0, 0);
        const maxDate = new Date(currentIndex.available_to);
        maxDate.setHours(23, 59, 59, 999);
        return { minDate, maxDate };
      }
    } catch (error) {
      console.error("Error parsing categories from localStorage:", error);
    }
    return { minDate: null, maxDate: null };
  };

  // Get the date range for the current index
  const { minDate: indexMinDate, maxDate: indexMaxDate } = getIndexDateRange(currentIndexId);

  // Check if selected dates are within the available range
  const isDateRangeValid = () => {
    if (!datePickerTimeFrom || !datePickerTimeTo) return true; // No dates selected is valid
    if (!indexMinDate || !indexMaxDate) return true; // No range restriction

    const selectedFrom = new Date(datePickerTimeFrom);
    const selectedTo = new Date(datePickerTimeTo);

    return selectedFrom >= indexMinDate && selectedTo <= indexMaxDate;
  };

  const dateRangeError = !isDateRangeValid();

  // Format date range for display
  const formatAvailableDateRange = () => {
    if (indexMinDate && indexMaxDate) {
      const formatDate = (date) => {
        const locale = i18n.language === 'ar' ? 'ar-SA-u-nu-latn' : 'en-US';
        return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
      };
      return `${t('Map.available_range')}: ${formatDate(indexMinDate)} - ${formatDate(indexMaxDate)}`;
    }
    return "";
  };

  const [fetchFaoImage, setFetchFaoImage] = useState(false);
  const polyStatsRequest = useApi(statsEndpoint);
  const polyImageRequest = useApi(imageEndpoint);
  const getTiffReq = useApi(tiffEndopoint);
  const indexDataRequest = useApi(getIndexData);
  const weatherStatsData = useSelector((state) => state.mapState.weatherStats);

  useEffect(() => {
    dispatch(setUser(JSON.parse(loggedInUser)));

    // Restore map file content if it exists
    const retrievedCompressed = localStorage.getItem("mapFileContent") ?? "";
    const savedMapContent = LZString.decompress(retrievedCompressed);
    if (savedMapContent) {
      dispatch(setFileContent(JSON.parse(savedMapContent)));
      dispatch(setUploaderFlag(true));
    }
  }, [loggedInUser, dispatch]);



  useLayoutEffect(() => {
    return () => {
      // Don't clear the entire map state on unmount
      // Only clear what's necessary
      toast.dismiss();
      const controller = new AbortController();
      controller.abort();
    };
  }, []);

  // Helper function to get error message based on language
  const getErrorMessage = (error, msg = "") => {
    if (error.response?.data) {
      const isArabic = i18n.language === "ar";
      return isArabic
        ? error.response.data.message_ar || error.response.data.message
        : error.response.data.message;
    }
    if (msg) return msg;
    return "An error occurred";
  };

  const updateIndex = async (index) => {
    try {
      const response = await axios.put(`${url}/indexes/${indexData.id}`, {
        index,
      });

      if (response.status === 200) {
        toast.success("Data fetched successfully");
      }
    } catch (error) {
      console.error("Error updating index:", error);
      const errorMessage = getErrorMessage(error, "Failed to update index");
      toast.error(errorMessage);
    }
  };

  /**
   * fetches the stats of area at index j
   * @param {*} j the index of area
   *
   */

  const getPolyXStats = async (j) => {
    if ("LandCover".includes(dataKey)) return;
    console.log("stats req begin");

    try {
      const res = await indexDataRequest.request(
        {
          id: currentIndexId,
          request_type: "stats",
          coordinates: Array.isArray(areas[j].coordinates)
            ? areas[j].coordinates
            : null,
          geo: Array.isArray(areas[j].coordinates)
            ? null
            : JSON.stringify(areas[j].coordinates),
          from_date: datePickerTimeFrom,
          to_date: datePickerTimeTo,
        },
        true
      );

      // Can't connect to server
      if (!res) {
        toast.error(t("request_error"));
        return;
      }

      if (res.status !== 200) {
        const errorMessage =
          res.data?.message || res.data?.errors || t("Map.stats_request_error");
        toast.error(errorMessage);
        return;
      }

      // Success
      console.log("stats res", res);
      dispatch(
        setAreaStats({
          polyId: areas[j].polyId,
          data: {
            timestamps: Object.values(res.data.data.TimeStamp),
            stats: Object.values(res.data.data.mean),
            mean: Object.values(res.data.data.mean),
            max: Object.values(res.data.data.max),
            min: Object.values(res.data.data.min),
            selected: "mean",
            unit: res.data.data.unit,
          },
        })
      );
    } catch (error) {
      console.error("Error fetching stats:", error);
      const errorMessage = getErrorMessage(error, t("Map.stats_request_error"));
      toast.error(errorMessage);
    }
  };

  /**
   * fetch tiles of j area from our server
   *
   * @param  {int} j the index of area
   */
  const getXImage = async (j) => {
    console.log("tiles req begin");

    try {
      const res = await indexDataRequest.request(
        {
          id: currentIndexId,
          request_type: "tiles",
          coordinates: Array.isArray(areas[j].coordinates)
            ? areas[j].coordinates
            : null,
          geo: Array.isArray(areas[j].coordinates)
            ? null
            : JSON.stringify(areas[j].coordinates),
          from_date: datePickerTimeFrom,
          to_date: datePickerTimeTo,
          land_cover_dataset: JSON.stringify(land_cover_dataset),
        },
        false
      );

      // Can't connect to server
      if (!res) {
        toast.error(t("request_error"));
        return;
      }

      if (res.status !== 200) {
        const errorMessage = getErrorMessage(
          { response: res },
          t("Map.image_request_error")
        );
        toast.error(errorMessage);
        return;
      }

      // Success
      dispatch(
        setAreaTiles({
          polyId: areas[j].polyId,
          tileUrl: res.data.tile_url_template,
          bbox: res.data.bounds,
          imagename: res.data.imagename,
        })
      );
    } catch (error) {
      console.error("Error fetching tiles:", error);
      const errorMessage = getErrorMessage(error, t("Map.image_request_error"));
      toast.error(errorMessage);
    }
  };

  const getTiffImage = async (j) => {
    console.log("tiff req begin");

    try {
      const response = await indexDataRequest.request(
        {
          id: currentIndexId,
          request_type: "tiff",
          coordinates: Array.isArray(areas[j].coordinates)
            ? areas[j].coordinates
            : null,
          geo: Array.isArray(areas[j].coordinates)
            ? null
            : JSON.stringify(areas[j].coordinates),
          from_date: datePickerTimeFrom,
          to_date: datePickerTimeTo,
          land_cover_dataset: JSON.stringify(land_cover_dataset),
        },
        false
      );

      console.log("tiff res", response);

      // Can't connect to server
      if (!response) {
        toast.error(t("request_error"));
        return;
      }

      if (response.status !== 200) {
        const errorMessage = getErrorMessage(
          { response: response },
          "Failed to download TIFF file"
        );
        toast.error(errorMessage);
        return;
      }

      // Success
      dispatch(setDownloadLink(response.data));
      const link = document.createElement("a");
      link.href = response.data;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("TIFF file downloaded successfully");
    } catch (error) {
      console.error("Error fetching TIFF:", error);
      const errorMessage = getErrorMessage(
        error,
        "Failed to download TIFF file"
      );
      toast.error(errorMessage);
    }
  };
  /**
   * checks whether to fetch image from fao or our api
   * then fetch images of given indexes
   * @param  {int} polyIndexes int indexes of images to fetch
   */

  const fetchImagesAtIdx = async (...polyIndexes) => {
    try {
      // Request images from our server
      const promises = polyIndexes.map((j) => getXImage(j));
      await Promise.allSettled(promises); // Use allSettled to handle individual errors
    } catch (error) {
      console.error("Error fetching images:", error);
      toast.error("Failed to fetch some images");
    }
  };

  const fetchTiffImage = async (index) => {
    if (areas.length === 0) {
      toast.warn("No areas selected");
      return;
    }

    setTiffLoading(true); // Start loading
    try {
      await getTiffImage(index);
    } catch (error) {
      console.error("Error in fetchTiffImage:", error);
      const errorMessage = getErrorMessage(error, "Failed to fetch TIFF image");
      toast.error(errorMessage);
    } finally {
      setTiffLoading(false); // End loading
    }
  };
  /**
   *
   * handles the user submit action, fetches the images and stats
   */
  const handleClickSubmit = async () => {
    // TODO: check if polygon is inside arab land
    if (areas.length === 0) return;

    if (areas.length > 2) {
      toast.warn("Please select only 2 areas");
      return;
    }

    setSubmitLoading(true);
    try {
      if (areas.length === 1) {
        // Fetch data for polygon 0
        await fetchImagesAtIdx(0);
        // await getPolyXStats(0); // Uncomment if needed
      }

      if (areas.length === 2) {
        if (JSON.stringify(chartData) === "{}") {
          // If no data exists call fetch for polygon 0 & 1
          await fetchImagesAtIdx(0, 1);
          // await getPolyXStats(0); // Uncomment if needed
          // await getPolyXStats(1); // Uncomment if needed
        } else {
          // Some data exists, means we need polygon 1 data
          await fetchImagesAtIdx(1);
          // await getPolyXStats(1); // Uncomment if needed
        }
      }
    } catch (error) {
      console.error("Error in handleClickSubmit:", error);
      const errorMessage = getErrorMessage(error, "Failed to process request");
      toast.error(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  /**
   * controls submit button enable state,
   * @returns boolean value to disable or enable button
   */
  const disableBtn = () => {
    if (submitLoading) return true;
    if (!datePickerTimeFrom || !datePickerTimeTo) return true;
    if (dateRangeError) return true;
    if (areas.length === 0) return true;
    if (areas.length > 2) return true;
    if (chartData?.series?.length >= 2) return true;
    return false;
  };

  const getChartDataMemoized = useMemo(
    () => () => getChartData(dataKey, loggedUserState, areas),
    [areas, dataKey, loggedUserState]
  );
  const getWeatherDataMemoized = useMemo(
    () => () => getWeatherChartdata(weatherStatsData, loggedUserState, t),
    [loggedUserState, t, weatherStatsData]
  );
  const chartData = isWeatherPage
    ? getWeatherDataMemoized()
    : getChartDataMemoized();

  return (
    <>
      <div className="monitoring-container">
        <div className="map-wrapper">
          {!isMobile && (
            <Stripe>
              <NavPageSelector route={route} />
              <div className="stripe-content">
                <LanguageSwitcher stripe={true} />
                <SignInComponent
                  menuIconColor="black"
                  backgroundColor="white"
                  classN="modalcontainer-base"
                  stripe={true}
                />
              </div>
            </Stripe>
          )}
          {
            <Stripe second={true}>
              <CoordinatesInput disableFileUpload={isWeatherPage} />
              {!isWeatherPage && dataKey && (
                <>
                  <div className="second-stripe-btns">
                    {isAdmin && (
                      <button
                        className="submit-compare-buttons"
                        onClick={handleOpen}
                      >
                        {t("Map.monitoring")}
                      </button>
                    )}

                    <button
                      className="submit-compare-buttons"
                      onClick={handleIndexInfoOpen}
                    >
                      {t("Map.index_info")}
                    </button>
                    {isAdmin && (
                      <button
                        className="submit-compare-buttons"
                        onClick={handleAddTiffOpen}
                      >
                        {t("Map.add_tiff")}
                      </button>
                    )}
                  </div>
                </>
              )}
              {dataKey && (
                <div className="stripe-select-date" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                  {/* Label and availability text - above the input */}
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
                    <p className="dateParagraph" style={{ margin: 0, marginBottom: '4px' }}>{t("Map.select_date")}</p>
                    {indexMinDate && indexMaxDate && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: dateRangeError ? 'error.main' : 'text.secondary',
                          fontSize: '0.7rem',
                          fontStyle: 'italic'
                        }}
                      >
                        {formatAvailableDateRange()}
                      </Typography>
                    )}
                  </div>

                  {/* Date picker and submit button row */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <CustomProvider
                        locale={i18n.language === "ar" ? arEG : undefined}
                        rtl
                      >
                        <DateRangePicker
                          size={isMobile ? "sm" : "default"}
                          placement="autoVerticalStart"
                          showOneCalendar={isMobile}
                          cleanable
                          appearance={isMobile ? "subtle" : "default"}
                          value={
                            datePickerTimeFrom && datePickerTimeTo
                              ? [new Date(datePickerTimeFrom), new Date(datePickerTimeTo)]
                              : null
                          }
                          // Open calendar at last available date instead of today
                          defaultCalendarValue={indexMaxDate ? [indexMaxDate, indexMaxDate] : undefined}
                          // Restrict selectable dates to available range
                          disabledDate={(date) => {
                            if (!indexMinDate || !indexMaxDate) return false;
                            return date < indexMinDate || date > indexMaxDate;
                          }}
                          // Add error styling when dates are out of range
                          style={{
                            borderColor: dateRangeError ? '#d32f2f' : undefined,
                            '--rs-input-border': dateRangeError ? '#d32f2f' : undefined
                          }}
                          className={dateRangeError ? 'date-range-error' : ''}
                          onOk={(date) => {
                            const fromDate = new Date(date[0]);
                            const toDate = new Date(date[1]);
                            fromDate.setHours(12, 0, 0, 0);
                            toDate.setHours(12, 0, 0, 0);
                            dispatch(setDatePickerFrom(fromDate.toISOString().split("T")[0]));
                            dispatch(setDatePickerTo(toDate.toISOString().split("T")[0]));
                          }}
                          onClean={() => dispatch(clearDates())}
                        />
                      </CustomProvider>
                      {/* Error message below the date picker */}
                      {dateRangeError && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'error.main',
                            fontSize: '0.7rem',
                            marginTop: '2px'
                          }}
                        >
                          {t("Map.date_out_of_range") || "Selected dates are out of range"}
                        </Typography>
                      )}
                    </div>
                    <button
                      onClick={handleClickSubmit}
                      className="submit-compare-buttons"
                      disabled={disableBtn()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        minWidth: '100px'
                      }}
                    >
                      {submitLoading ? (
                        <>
                          <CircularProgress size={16} color="inherit" />
                          <span>{t("loading")}</span>
                        </>
                      ) : (
                        t("submit")
                      )}
                    </button>
                  </div>
                </div>
              )}
            </Stripe>
          }

          {isMobile && <NavPageSelector route={route} />}
          {/* 
          {dataKey && isMobile && (
          <div className="dates">
            <p className="dateParagraph">{t("Map.select_date")}</p>
            <DateRangePicker
            size={isMobile ? "sm" : "default"} 
            placement="autoVerticalStart" 
            showOneCalendar={isMobile}
            cleanable
            appearance={isMobile ? "subtle" : "default"} 
            onOk={(date) => {
              if (date && date.length === 2) {
                const fromDate = new Date(date[0]);
                const toDate = new Date(date[1]);
                fromDate.setHours(12, 0, 0, 0);
                toDate.setHours(12, 0, 0, 0);

                dispatch(
                  setDatePickerFrom(fromDate.toISOString().split("T")[0])
                );
                dispatch(
                  setDatePickerTo(toDate.toISOString().split("T")[0])
                );
              }
            }}
              onClean={() => dispatch(clearDates())}
            />
            {!isMobile && 
          <button
            onClick={handleClickSubmit}
            className="submit-compare-buttons"
            disabled={disableBtn()}
          >
            {t("submit")}
          </button>
          }
          </div>)} */}

          {/* {isMobile && <CoordinatesInput disableFileUpload={isWeatherPage} />} */}
          {/* <CoordinatesInput disableFileUpload={isWeatherPage} /> */}
          <Map
            legendData={legendData}
            index={dataKey}
            hideButtons={true}
            showEditControl={!isWeatherPage}
          />
        </div>
      </div>

      {!isWeatherPage && dataKey && (
        <div className="submit-compare-buttons-container button-container">
          {/*{isMobile && 
          <>
          <button
            onClick={handleClickSubmit}
            className="submit-compare-buttons"
            disabled={disableBtn()}
          >
            {t("submit")}
          </button>
          
          {isAdmin && <button
            className="submit-compare-buttons"
            onClick={handleOpen}
          >
            Monitoring
          </button>}
          <button
            className="submit-compare-buttons"
            onClick={handleIndexInfoOpen}
          >
            Index Info
          </button>
          {isAdmin && <button
            className="submit-compare-buttons"
            onClick={handleAddTiffOpen}
          >
            Add Tiff
          </button>}
          </>}*/}
          <IndexInfoDialog
            open={indexInfoOpen}
            index={indexData}
            onSave={updateIndex}
            onClose={() => setIndexInfoOpen(false)}
          />
          <AddTiffDialog
            open={addTiffOpen}
            index={indexData}
            onClose={() => setAddTiffOpen(false)}
          />
          <LandDegradationDialog
            mainIndex={mainIndex}
            open={landDegradationOpen}
            onClose={() => setLandDegradationOpen(false)}
          />
          <MonitoringDialog
            mainIndex={mainIndex}
            open={open}
            handleClose={handleClose}
          />
        </div>
      )}

      {/* {isMobile && (
        <div className="mobile-popup-container">
          <div className="mobile-popup-icon-container">
            <img src={MobileIcon} className="mobile-popup-icon" />
            <span className="mobile-popup-text">
              This site is not suited for mobile use , to enjoy it more , use a
              desktop device{" "}
            </span>
          </div>
          <button
            className="mobile-popup-button"
            onClick={() => setIsMobile(false)}
          >
            OK
          </button>
        </div>
      )} */}

      {/* Right-side panel: buttons + image info */}
      {areas && areas.length > 0 && (
        <RightSidePanel
          areas={areas}
          showOverlay={showOverlay}
          dispatch={dispatch}
          t={t}
          fetchTiffImage={fetchTiffImage}
          tiffLoading={tiffLoading}
          datePickerTimeFrom={datePickerTimeFrom}
          datePickerTimeTo={datePickerTimeTo}
        />
      )}

      {/* <CollapsibleChart chartData={chartData} datakey={dataKey} /> */}

      {/* {!isWeatherPage && (

      )} */}
    </>
  );
}
